/**
 * Per-scope API router for the PocketBase → Django migration (Phase 1 §10).
 *
 * The whole app shares ONE PocketBase auth store (see lib/pb.ts). During the
 * strangler-fig cutover we flip individual route families ("scopes") from PB
 * to Django one at a time behind an env flag, with zero frontend logic change
 * beyond swapping `pb.collection(x)` → `pbFor("<scope>").collection(x)` in each
 * page module's `apis/api.ts`.
 *
 * How it works
 * ------------
 * - `VITE_DJANGO_ROUTES` is a comma list of scopes currently served by Django,
 *   e.g. "auth,workspaces". Empty = everything still on PB (default).
 * - For a Django scope, `pbFor(scope)` returns a PocketBase client whose base
 *   URL points at that app's Django prefix (`/dj/<app>`), so the SDK's
 *   `/api/collections/<coll>/records` tail resolves to the Django endpoint the
 *   backend agents built. It SHARES the global `pb.authStore`, so the Bearer
 *   token flows unchanged — Django's DualAuthBackend accepts both the legacy
 *   PB JWT and the new Django JWT through the migration window (§6).
 * - For a PB scope, it returns the existing global `pb` instance untouched.
 *
 * Rollback for any scope = drop it from `VITE_DJANGO_ROUTES` and redeploy the
 * frontend. No backend change, no data change. ~3 min including rebuild.
 *
 * Required env (set per environment / GHA secret):
 *   VITE_DJANGO_BASE   e.g. https://canvaloop.visualoop.co.uk/dj
 *   VITE_DJANGO_ROUTES e.g. auth,workspaces            (default: "")
 *   VITE_API_BASE      API origin (Django backend)
 */

import PocketBase from "pocketbase";
import pb from "../pb";

/**
 * Cutover scopes, in the §10 lowest-blast-radius-first order. `webhooks` is
 * server-only (Caddy re-routes it directly — listed for completeness).
 */
export type ApiScope =
  | "webhooks"
  | "auth"
  | "workspaces"
  | "brand_elements"
  | "presets"
  | "assets"
  | "generation"
  | "billing"
  | "usage"
  | "notifications"
  | "scheduling";

/**
 * Django mount prefix per scope. Mirrors `backend-django/canvaloop/urls.py`:
 * each app is included at `/dj/<prefix>/` and the PB SDK appends its own
 * `/api/collections/<coll>/records` (or `/api/cl/...`) tail beneath it.
 */
const DJANGO_SCOPE_PREFIX: Record<ApiScope, string> = {
  webhooks: "",
  auth: "/auth",
  workspaces: "/workspaces",
  brand_elements: "/brand-elements",
  presets: "/presets",
  assets: "/assets",
  generation: "/generation",
  billing: "/billing",
  usage: "/usage",
  notifications: "/notifications",
  scheduling: "/scheduling",
};

// API origin (Django backend) — see lib/pb.ts.
const PB_BASE =
  (import.meta.env.VITE_API_BASE as string) || "http://127.0.0.1:8000";

const DJANGO_BASE = (
  (import.meta.env.VITE_DJANGO_BASE as string) || ""
).replace(/\/+$/, "");

const DJANGO_ROUTES = new Set(
  ((import.meta.env.VITE_DJANGO_ROUTES as string) ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean),
);

/** True once a scope has been flipped to Django via VITE_DJANGO_ROUTES. */
export function isDjango(scope: ApiScope): boolean {
  return DJANGO_ROUTES.has(scope);
}

/**
 * Resolved base URL for a scope — Django app prefix when flipped, else the
 * legacy PB base. Useful for raw `pb.send()` / non-collection calls.
 */
export function apiBase(scope: ApiScope): string {
  if (!isDjango(scope)) return PB_BASE;
  if (!DJANGO_BASE) {
    console.warn(
      `[router] scope "${scope}" is flagged for Django but VITE_DJANGO_BASE ` +
        `is empty — falling back to PB to avoid a hard break.`,
    );
    return PB_BASE;
  }
  return `${DJANGO_BASE}${DJANGO_SCOPE_PREFIX[scope]}`;
}

// Scope-bound PB clients are memoised by base URL. They all share the global
// `pb.authStore`, so login/refresh on any one (or on the global `pb`) keeps
// every client authenticated with the same token.
const _clients = new Map<string, PocketBase>();

/**
 * The PocketBase client a given scope's calls should go through.
 *
 * - PB scope  → the shared global `pb` (unchanged behaviour).
 * - Django scope → a memoised client pointed at `/dj/<app>`, sharing
 *   `pb.authStore` so the Bearer token + record stay consistent.
 *
 * Usage in a page module's apis/api.ts (the only cutover change):
 *   import { pbFor } from "@/lib/api/router";
 *   const api = pbFor("workspaces");
 *   api.collection("workspaces").getFullList(...)
 */
export function pbFor(scope: ApiScope): PocketBase {
  if (!isDjango(scope)) return pb;

  const base = apiBase(scope);
  if (base === PB_BASE) return pb; // misconfig fallback (warned above)

  let client = _clients.get(base);
  if (!client) {
    // 2nd ctor arg = shared auth store → one token across PB + all Django scopes.
    client = new PocketBase(base, pb.authStore);
    client.autoCancellation(false); // match lib/pb.ts
    _clients.set(base, client);
  }
  return client;
}

export default pbFor;

// ── Cutover log (one line per live scope flip; the actual switch is the
// VITE_DJANGO_ROUTES GHA secret — this just gives a deterministic
// frontend rebuild + an audit trail). Rollback = unset the scope in the
// secret and redeploy. ──
// 2026-05-15  workspaces      → Django ✅ (stays). Byte-parity proven
//             (1634/1635; only inherent live pwa last_seen drift).
//             Auth + rule-parity (admin-sees-all) + faithful
//             timestamps + file-refs all fixed forward.
// 2026-05-15  brand_elements  → Django ✅ (Brand Kit; 3/3 imgs render,
//             0 console errors, admin-all OK).
// 2026-05-15  presets         → Django ✅ (saved_ideas 200, 0 errors).
// 2026-05-15  usage,notifs    → Django (logs/push; safe — no file
//             bytes, minor log staleness acceptable).
// DEFERRED: assets+generation flip TOGETHER and need the Spaces file
//   migration (#27, DO creds) — flipping assets alone breaks uploads
//   AND makes new (PB-side) generations missing from the DJ library
//   until re-sync. auth: pocketbaseAdapter login/refresh still on PB.
